import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Chip,
  Paper,
  Rating,
  Avatar,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  ArrowBackIos as ArrowLeftIcon,
  ArrowForwardIos as ArrowRightIcon,
  FiberManualRecord as DotIcon,
  LocalOffer as OfferIcon,
  FavoriteBorder as FavoriteIcon,
  Favorite as FavoriteFilledIcon,
  LocationOn,
} from '@mui/icons-material';
import * as Icons from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { productsApi } from '../api/products';
import { featuredTeamsApi } from '../api/featuredTeams';
import accountService from '../services/accountService';
import SmartRecommendations from '../components/SmartRecommendations';
import placeholderImage from '../../images/No-Image-Placeholder.png';
import Seo from '../components/Seo';
import { getTemplateStyle } from '../theme/templateStyles';
import { isProductOutOfStock } from '../utils/stock';

const HOME_SECTION_KEYS = ['hero', 'trust', 'teams', 'products', 'community', 'testimonials', 'recommendations'];

const Home = () => {
  const navigate = useNavigate();
  const templateName = useSelector((state) => state.template?.template);
  const tplStyle = getTemplateStyle(templateName);
  const heroVariant = tplStyle.hero || 'slider';
  // Sports and unsaved stores keep the original design untouched
  const isOriginalLook = !templateName || templateName === 'Sports' || templateName === 'default';

  // Section order + visibility per template, applied through flex `order`
  const sectionSx = (key) => {
    const configured = tplStyle.home?.order?.indexOf(key);
    return {
      order: configured === undefined || configured === -1 ? HOME_SECTION_KEYS.indexOf(key) : configured,
      ...(tplStyle.home?.hide?.includes(key) ? { display: 'none' } : {}),
    };
  };

  const bannersConfig = useSelector((state) => state.template?.banners);
  const businessName = useSelector((state) => state.configs?.businessName);
  const logoConfig = useSelector((state) => state.template?.logo);
  const featuredTeamsEnabled = useSelector((state) => state.configs?.featuredTeamsEnabled);
  const trustBadges = useSelector((state) => state.template?.trustBadges);
  const testimonials = useSelector((state) => state.template?.testimonials);
  const fanCommunity = useSelector((state) => state.template?.fanCommunity);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [wishlist, setWishlist] = useState(new Set());
  const [loadingWishlistItems, setLoadingWishlistItems] = useState(new Set());
  const [productSections, setProductSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [apiTeams, setApiTeams] = useState([]);

  // Handle broken images
  const handleImageError = (e) => {
    e.target.onerror = null; // Prevent infinite loop
    e.target.src = placeholderImage;
  };

  // Fetch featured teams from API
  const loadFeaturedTeams = async () => {
    try {
      const teams = await featuredTeamsApi.getFeaturedTeams();
      if (teams && teams.length > 0) {
        setApiTeams(teams);
      }
    } catch (error) {
      console.error('Error loading featured teams:', error);
    }
  };

  // Use API teams only - no fallback content
  const featuredTeams = apiTeams;

  // Use configured banners from Redux; without any, show one slide tailored from business config.
  // A banner renders if it has an image OR any text content — gradient-only banners (no image)
  // still show via the gradient overlay below, so they must not be filtered out.
  const configuredSlides = (bannersConfig?.items || []).filter(
    (item) => item && (item.imageUrl || item.title || item.subtitle || item.description)
  );
  const isDefaultHero = configuredSlides.length === 0;
  const heroSlides = configuredSlides.length > 0 ? configuredSlides : [{
    id: 'default-hero',
    title: businessName || 'Welcome to Our Store',
    subtitle: logoConfig?.slogan || '',
    description: 'Discover our latest products and offers.',
    buttonText: 'Shop Now',
    buttonLink: '/products',
    imageUrl: '',
    gradient: 'linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 55%, #000) 100%)',
  }];
  const safeCurrentSlide = heroSlides.length > 0 ? currentSlide % heroSlides.length : 0;

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // Fetch featured teams when component mounts or when feature is enabled
  useEffect(() => {
    if (featuredTeamsEnabled) {
      loadFeaturedTeams();
    }
  }, [featuredTeamsEnabled]);

  // Auto-rotate slides
  useEffect(() => {
    if (heroSlides.length < 2) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const groupedProducts = await productsApi.getGroupedProducts();

      // Transform grouped products into sections for display
      const sections = groupedProducts.map((category) => ({
        title: category.categoryName,
        link: 'Shop Collection',
        category: category.categoryName.toLowerCase().replace(/\s+/g, '-'),
        categoryId: category.categoryId,
        products: category.products.slice(0, 5).map((product, index) => ({
          id: product._id,
          name: product.productName,
          price: `KSH ${product.defaultPrice?.toLocaleString() || '0'}`,
          normalPrice: product.defaultNormalPrice,
          image: product.featuredImage || placeholderImage,
          hasVariations: product.hasVariations,
          pricing: product.pricing,
          productCode: product.productCode,
          subcategoryName: product.subcategoryName,
          parentCategoryName: product.parentCategoryName
        }))
      }));

      setProductSections(sections);
    } catch (error) {
      console.error('Error fetching products:', error);
      // Fallback to empty sections
      setProductSections([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  const handleToggleWishlist = async (product, e) => {
    e.stopPropagation();
    const productId = product.id || product._id;

    // Add to loading set
    setLoadingWishlistItems(prev => new Set([...prev, productId]));

    try {
      const result = await accountService.toggleWishlistItem(product);

      if (result.success) {
        // Update wishlist Set
        setWishlist(prev => {
          const newSet = new Set(prev);
          if (result.inWishlist) {
            newSet.add(productId);
          } else {
            newSet.delete(productId);
          }
          return newSet;
        });
        setSnackbarMessage(result.message || (result.inWishlist ? 'Added to Wishlist ❤️' : 'Removed from Wishlist'));
      } else {
        setSnackbarMessage(result.error || 'Failed to update wishlist');
      }
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      setSnackbarMessage('Error updating wishlist');
      setSnackbarOpen(true);
    } finally {
      // Remove from loading set
      setLoadingWishlistItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <Seo description={`Shop online at ${businessName || 'our store'}. Quality products, fast delivery, secure payment.`} />
      {/* Hero Carousel Section - treatment varies with the store template */}
      {heroSlides.length > 0 && (
      <Box
        sx={{
          position: 'relative',
          height: isDefaultHero ? { xs: '32vh', md: '40vh' } : { xs: '70vh', md: '85vh' },
          minHeight: isDefaultHero ? '280px' : undefined,
          overflow: 'hidden',
          backgroundColor: '#000',
          ...(heroVariant === 'gradient' && {
            background: 'linear-gradient(135deg, var(--primary) 0%, #10151c 100%)',
          }),
          ...(heroVariant === 'rounded' && {
            mx: { xs: 1.5, sm: 2.5, md: 4 },
            mt: { xs: 1.5, md: 2.5 },
            borderRadius: { xs: '16px', md: '24px' },
          }),
          ...sectionSx('hero'),
        }}
      >
        {/* Slides Container */}
        <Box
          sx={{
            display: 'flex',
            transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: `translateX(-${safeCurrentSlide * 100}%)`,
            height: '100%',
          }}
        >
          {heroSlides.map((slide, index) => (
            <Box
              key={slide.id ?? index}
              sx={{
                minWidth: '100%',
                height: '100%',
                position: 'relative',
                backgroundImage: heroVariant === 'split'
                  ? 'none'
                  : (slide.imageUrl || slide.image) ? `url(${slide.imageUrl || slide.image})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: heroVariant === 'split'
                    ? 'linear-gradient(120deg, var(--primary) 0%, #0d1117 100%)'
                    : slide.gradient,
                },
              }}
            >
              <Container
                maxWidth="lg"
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  zIndex: 1,
                  ...(heroVariant === 'editorial' && { justifyContent: 'center' }),
                  ...(heroVariant === 'split' && { gap: 4, justifyContent: 'space-between' }),
                }}
              >
                <Box
                  sx={{
                    color: '#fff',
                    maxWidth: '600px',
                    ...(heroVariant === 'editorial' && { textAlign: 'center', maxWidth: '760px' }),
                    animation: safeCurrentSlide === index ? 'slideIn 1s ease-out' : 'none',
                    '@keyframes slideIn': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateY(30px)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateY(0)',
                      },
                    },
                  }}
                >
                  {/* Badge and Offer */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {slide.badge && (
                      <Chip
                        label={slide.badge}
                        size="small"
                        sx={{
                          backgroundColor: '#facc15',
                          color: '#000',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    )}
                    {slide.offer && (
                      <Chip
                        label={slide.offer}
                        size="small"
                        icon={<OfferIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          color: '#fff',
                          border: '1px solid rgba(255,255,255,0.3)',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          '& .MuiChip-icon': {
                            color: '#fff',
                          },
                        }}
                      />
                    )}
                  </Box>

                  <Typography
                    sx={{
                      fontSize: { xs: '12px', sm: '14px', md: '16px' },
                      fontWeight: 600,
                      letterSpacing: { xs: '1.5px', sm: '2px', md: '2.5px' },
                      mb: 1,
                      textTransform: 'uppercase',
                      opacity: 0.9,
                    }}
                  >
                    {slide.subtitle}
                  </Typography>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: isDefaultHero
                        ? { xs: '1.75rem', sm: '2rem', md: '2.75rem' }
                        : { xs: '2.5rem', sm: '3rem', md: '4rem', lg: '5rem' },
                      fontWeight: 900,
                      lineHeight: 1.1,
                      mb: 2,
                      textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                      ...(heroVariant === 'editorial' && {
                        fontWeight: 300,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        textShadow: 'none',
                      }),
                    }}
                  >
                    {slide.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                      mb: 3,
                      opacity: 0.95,
                      maxWidth: { xs: '400px', sm: '500px' },
                      lineHeight: 1.5,
                      fontWeight: 500,
                    }}
                  >
                    {slide.description}
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate(slide.buttonLink)}
                    sx={{
                      backgroundColor: '#fff',
                      color: '#000',
                      fontSize: { xs: '12px', sm: '14px', md: '16px' },
                      fontWeight: 600,
                      px: { xs: 2.5, sm: 3, md: 4 },
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      borderRadius: 0,
                      textTransform: 'uppercase',
                      letterSpacing: { xs: '0.5px', sm: '0.75px', md: '1px' },
                      transition: 'all var(--motion-speed, 0.3s) var(--motion-ease, ease)',
                      '&:hover': {
                        backgroundColor: '#f0f0f0',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                      },
                      ...(heroVariant === 'editorial' && {
                        backgroundColor: 'transparent',
                        color: '#fff',
                        border: '1px solid rgba(255,255,255,0.85)',
                        '&:hover': {
                          backgroundColor: '#fff',
                          color: '#000',
                          transform: 'none',
                          boxShadow: 'none',
                        },
                      }),
                      ...(heroVariant === 'rounded' && {
                        borderRadius: 'var(--button-border-radius, 12px)',
                      }),
                    }}
                  >
                    {slide.buttonText}
                  </Button>
                </Box>

                {/* Split hero: product/banner image as a contained panel */}
                {heroVariant === 'split' && (slide.imageUrl || slide.image) && (
                  <Box
                    component="img"
                    src={slide.imageUrl || slide.image}
                    alt={slide.title || 'Banner'}
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      maxWidth: '42%',
                      maxHeight: '78%',
                      objectFit: 'contain',
                      borderRadius: '12px',
                      boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                    }}
                  />
                )}
              </Container>
            </Box>
          ))}
        </Box>

        {/* Navigation Arrows */}
        {heroSlides.length > 1 && (
        <>
        <IconButton
          onClick={handlePrevSlide}
          sx={{
            position: 'absolute',
            left: { xs: 10, md: 30 },
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: '#fff',
            backdropFilter: 'blur(10px)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
            },
            zIndex: 2,
          }}
        >
          <ArrowLeftIcon />
        </IconButton>
        <IconButton
          onClick={handleNextSlide}
          sx={{
            position: 'absolute',
            right: { xs: 10, md: 30 },
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: '#fff',
            backdropFilter: 'blur(10px)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
            },
            zIndex: 2,
          }}
        >
          <ArrowRightIcon />
        </IconButton>

        {/* Dots Indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 30,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1.5,
            zIndex: 2,
          }}
        >
          {heroSlides.map((_, index) => (
            <IconButton
              key={index}
              onClick={() => handleDotClick(index)}
              sx={{
                p: 0,
                color: safeCurrentSlide === index ? '#fff' : 'rgba(255,255,255,0.4)',
                transform: safeCurrentSlide === index ? 'scale(1.2)' : 'scale(1)',
                transition: 'all 0.3s',
              }}
            >
              <DotIcon sx={{ fontSize: 12 }} />
            </IconButton>
          ))}
        </Box>
        </>
        )}
      </Box>
      )}

      {/* Trust Badges */}
      {trustBadges?.enabled && trustBadges?.items?.length > 0 && (
      <Box
        sx={{
          backgroundColor: 'var(--surface-tint, #f8f8f8)',
          py: 3,
          borderBottom: '1px solid #e0e0e0',
          ...sectionSx('trust'),
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-around', gap: 2, flexWrap: 'nowrap' }}>
            {trustBadges.items.map((badge) => {
              const BadgeIcon = Icons[badge.icon] || Icons.VerifiedOutlined;
              return (
                <Box
                  key={badge.id}
                  sx={{
                    flex: 1,
                    p: 2,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <BadgeIcon sx={{ fontSize: 32, color: 'var(--primary)' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {badge.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {badge.subtitle}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Container>
      </Box>
      )}

      {/* Featured Teams */}
      {featuredTeamsEnabled && featuredTeams.length > 0 && <Box sx={{
        py: { xs: 2, md: 3 },
        background: 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 4%, #fff) 0%, color-mix(in srgb, var(--primary) 12%, #fff) 100%)',
        position: 'relative',
        overflow: 'hidden',
        ...sectionSx('teams'),
      }}>
        <Container maxWidth="lg">
          <Box sx={{
            textAlign: 'center',
            mb: 2,
          }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.25rem', md: '1.75rem' },
                mb: 0.5,
                color: '#1A1A1A',
              }}
            >
              Wear Your
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  ml: 1,
                }}
              >
                Pride
              </Box>
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                maxWidth: 400,
                mx: 'auto',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
              }}
            >
              Choose your team. Wear your passion. Live the game.
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            gap: { xs: 1, sm: 1.5, md: 2 },
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            {featuredTeams.map((team) => (
              <Paper
                key={team.id}
                elevation={0}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 3,
                  cursor: 'pointer',
                  background: team.gradient,
                  width: { xs: 100, sm: 110, md: 120 },
                  height: { xs: 100, sm: 110, md: 120 },
                  flex: '0 0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                  transform: 'scale(1)',
                  '&:hover': {
                    transform: 'scale(1.05) translateY(-2px)',
                    boxShadow: '0 20px 30px rgba(0,0,0,0.12)',
                  },
                }}
                onClick={() => navigate(`/products?team=${team.id}`)}
              >
                {team.badge && (
                  <Chip
                    label={team.badge}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      color: team.badgeColor || '#000',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      height: 24,
                    }}
                  />
                )}
                <Typography
                  sx={{
                    color: team.textColor || 'white',
                    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                    fontWeight: 900,
                    letterSpacing: '-0.02em',
                    textShadow: team.textColor === '#1e3a8a'
                      ? '1px 1px 3px rgba(0,0,0,0.1)'
                      : '2px 2px 8px rgba(0,0,0,0.2)',
                    lineHeight: 1,
                  }}
                >
                  {team.code}
                </Typography>
                <Typography
                  sx={{
                    color: team.textColor || 'white',
                    fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                    fontWeight: 600,
                    mt: 0.25,
                  }}
                >
                  {team.name}
                </Typography>
              </Paper>
            ))}
          </Box>
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/featured-teams')}
              sx={{
                background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 50,
                fontSize: '1rem',
                boxShadow: '0 4px 14px color-mix(in srgb, var(--primary) 30%, transparent)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px color-mix(in srgb, var(--primary) 40%, transparent)',
                  background: 'linear-gradient(135deg, var(--button-hover) 0%, #018078 100%)',
                },
              }}
            >
              Browse All Teams →
            </Button>
          </Box>
        </Container>
      </Box>}

      {/* Product Sections */}
      <Box sx={sectionSx('products')}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : productSections.length > 0 ? (
        productSections.map((section, index) => (
        <Box key={index} sx={{ py: { xs: 3, sm: 4, md: 6 }, borderBottom: '1px solid #e0e0e0' }}>
          <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2, md: 3, lg: 4 } }}>
            <Box sx={{
              display: 'flex',
              flexDirection: tplStyle.sectionHeader === 'centered' ? 'column' : { xs: 'column', sm: 'row' },
              justifyContent: tplStyle.sectionHeader === 'centered' ? 'center' : 'space-between',
              alignItems: tplStyle.sectionHeader === 'centered' ? 'center' : { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 0 },
              mb: { xs: 2.5, sm: 3, md: 4 },
            }}>
              <Typography sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                fontWeight: isOriginalLook ? 300 : tplStyle.headingWeight,
                letterSpacing: isOriginalLook ? '0.05em' : tplStyle.headingSpacing,
                textTransform: 'uppercase',
                color: '#000',
                textAlign: tplStyle.sectionHeader === 'centered' ? 'center' : 'left',
              }}>
                {section.title}
              </Typography>
              <Button
                variant="text"
                onClick={() => navigate(`/products?category=${section.category}`)}
                sx={{
                  color: '#000',
                  textDecoration: 'underline',
                  textTransform: 'none',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  alignSelf: { xs: 'flex-end', sm: 'auto' },
                  whiteSpace: 'nowrap',
                }}
              >
                {section.link}
              </Button>
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
                lg: 'repeat(5, 1fr)',
              },
              gap: { xs: 0.75, sm: 1, md: 1.5, lg: 2 },
            }}>
              {section.products.map((product) => (
                <Box
                  key={product.id}
                  onClick={() => navigate(`/products/${product.id}`)}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    backgroundColor: '#f8f8f8',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    },
                    '& .quick-add-btn': {
                      opacity: 0,
                      transform: 'translateY(100%)',
                      transition: 'all 0.3s ease',
                    },
                    '&:hover .quick-add-btn': {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  <Box sx={{
                    position: 'relative',
                    paddingTop: '120%',
                    overflow: 'hidden',
                    backgroundColor: '#f8f8f8',
                  }}>
                    <Box
                      component="img"
                      src={product.image || placeholderImage}
                      alt={product.name}
                      onError={handleImageError}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />

                    {/* Out of Stock Badge - shown when every variation is out of stock */}
                    {isProductOutOfStock(product) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          backgroundColor: 'rgba(0,0,0,0.75)',
                          color: '#fff',
                          px: { xs: 0.75, sm: 1 },
                          py: { xs: 0.25, sm: 0.4 },
                          fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                          fontWeight: 600,
                          letterSpacing: '0.02em',
                          textTransform: 'uppercase',
                          zIndex: 3,
                          borderRadius: '4px',
                        }}
                      >
                        Out of Stock
                      </Box>
                    )}

                    {/* Wishlist Heart Button */}
                    <IconButton
                      onClick={(e) => handleToggleWishlist(product, e)}
                      disabled={loadingWishlistItems.has(product.id)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        '&:hover': { backgroundColor: '#fff' },
                        width: 32,
                        height: 32,
                        '&:disabled': { opacity: 0.7 },
                      }}
                    >
                      {loadingWishlistItems.has(product.id) ? (
                        <CircularProgress size={16} sx={{ color: '#dc2626' }} />
                      ) : wishlist.has(product.id) ? (
                        <FavoriteFilledIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                      ) : (
                        <FavoriteIcon sx={{ fontSize: 16, color: '#999' }} />
                      )}
                    </IconButton>

                    {/* Quick Add Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        p: 1.5,
                        opacity: 0,
                        transform: 'translateY(100%)',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                        zIndex: 10,
                      }}
                      className="quick-add-btn"
                    >
                      <Button
                        fullWidth
                        variant="text"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/products/${product.id}`);
                        }}
                        sx={{
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          fontWeight: 500,
                          py: 0.75,
                          px: 1.5,
                          borderRadius: '6px',
                          color: '#333',
                          border: '1px solid #e0e0e0',
                          backgroundColor: 'white',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'var(--primary)',
                            color: 'white',
                            borderColor: 'var(--primary)',
                          },
                        }}
                      >
                        View Product
                      </Button>
                    </Box>
                  </Box>

                  {/* Product Info */}
                  <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
                    <Typography
                      onClick={() => navigate(`/products/${product.id}`)}
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                        fontWeight: 400,
                        mb: { xs: 0.25, sm: 0.5 },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        lineHeight: { xs: 1.2, sm: 1.3 },
                        color: '#000',
                      }}>
                      {product.name}
                    </Typography>
                    <Typography
                      onClick={() => navigate(`/products/${product.id}`)}
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                        fontWeight: 600,
                        cursor: 'pointer',
                        lineHeight: { xs: 1.2, sm: 1.3 },
                        color: '#000',
                      }}>
                      {product.price}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
      ))
      ) : null}
      </Box>

      {/* Fan Community Section */}
      {fanCommunity?.enabled && fanCommunity?.posts?.length > 0 && (
      <Box sx={{
        py: { xs: 4, md: 6 },
        background: 'linear-gradient(135deg, var(--surface-tint, #f8f9fa) 0%, #ffffff 100%)',
        borderTop: '1px solid #e9ecef',
        ...sectionSx('community'),
      }}>
        <Container maxWidth="lg">
          {/* Header with Title and Stats */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 4, mb: 4 }}>
            {/* Left: Title & Description */}
            <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  mb: 1,
                  background: 'linear-gradient(135deg, #1a1a1a 0%, var(--primary) 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1.2,
                }}
              >
                {fanCommunity.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#6c757d',
                  fontSize: '1rem',
                  lineHeight: 1.5,
                  maxWidth: { xs: '100%', md: 400 },
                }}
              >
                {fanCommunity.subtitle}
              </Typography>
            </Box>

            {/* Right: Compact Stats */}
            {fanCommunity.stats?.length > 0 && (
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                {fanCommunity.stats.map((stat, index) => (
                  <Box key={index} sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontWeight: 800, color: 'var(--primary)', fontSize: '1.5rem' }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6c757d', fontWeight: 600 }}>
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Curated Community Content Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: { xs: 2, md: 3 }, mb: 4 }}>
            {fanCommunity.posts.map((post) => (
                <Box
                  key={post.id}
                  sx={{
                    position: 'relative',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    width: '100%',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                      '& .overlay': {
                        opacity: 1,
                      }
                    },
                  }}
                >
                  {/* Post Image */}
                  <Box
                    component="img"
                    src={post.imageUrl || placeholderImage}
                    alt={`${post.handle} in ${post.location}`}
                    onError={handleImageError}
                    sx={{
                      width: '100%',
                      height: { xs: 120, sm: 140, md: 160 },
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                    }}
                  />

                  {/* Platform Badge */}
                  {post.platform && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        background: post.platform === 'instagram'
                          ? 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)'
                          : 'linear-gradient(135deg, #000000 0%, #333333 100%)',
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Box sx={{ fontSize: '0.8rem' }}>
                        {post.platform === 'instagram' ? '📷' : '🎵'}
                      </Box>
                      {post.platform === 'instagram' ? 'IG' : 'TT'}
                    </Box>
                  )}

                  {/* Location Tag */}
                  {post.location && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '12px',
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Box sx={{ fontSize: '0.7rem' }}>📍</Box>
                      {post.location}
                    </Box>
                  )}

                  {/* Hover Overlay */}
                  <Box
                    className="overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0.1) 100%)',
                      opacity: 0,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      p: 1.5,
                      color: 'white',
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', mb: 0.5 }}>
                      {post.handle}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.2, opacity: 0.9 }}>
                      {post.location}
                    </Typography>
                  </Box>
                </Box>
            ))}
          </Box>

          {/* Share Your Jersey Moment CTA Section */}
          <Box
            sx={{
              mt: 4,
              p: 3,
              backgroundColor: '#f8f9fa',
              borderRadius: '16px',
              textAlign: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ fontSize: '1.75rem' }}>📸</Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#1a1a1a',
                }}
              >
                Share Your Jersey Moment
              </Typography>
            </Box>

            {fanCommunity.hashtag && (
              <Typography
                variant="body2"
                sx={{
                  mb: 3,
                  color: '#6c757d',
                  fontSize: '0.95rem',
                }}
              >
                Tag us with <strong>{fanCommunity.hashtag}</strong> for a chance to be featured!
              </Typography>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                  px: 3,
                  py: 1,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '25px',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 30%, transparent)',
                  },
                }}
              >
                📧 Submit Photo
              </Button>

              <Button
                variant="outlined"
                size="medium"
                sx={{
                  color: '#E4405F',
                  borderColor: '#E4405F',
                  px: 3,
                  py: 1,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '25px',
                  '&:hover': {
                    backgroundColor: 'rgba(228, 64, 95, 0.05)',
                    borderColor: '#E4405F',
                  },
                }}
              >
                📷 Instagram
              </Button>

              <Button
                variant="outlined"
                size="medium"
                sx={{
                  color: '#000',
                  borderColor: '#000',
                  px: 3,
                  py: 1,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '25px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    borderColor: '#000',
                  },
                }}
              >
                🎵 TikTok
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
      )}

      {/* Trust Builders Section */}
      {testimonials?.enabled && testimonials?.items?.length > 0 && (
      <Box sx={{
        py: { xs: 4, sm: 6, md: 8 },
        backgroundColor: 'var(--surface-tint, #f8f9fa)',
        ...sectionSx('testimonials'),
      }}>
        <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Section Title */}
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 700,
              textAlign: 'center',
              mb: { xs: 4, md: 6 },
              background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            What Our Customers Say
          </Typography>

          {/* Customer Testimonials */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 4, mb: 8 }}>
            {testimonials.items.map((testimonial, index) => (
              <Paper
                key={testimonial.id ?? index}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '12px',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                {/* Customer Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    src={testimonial.avatarUrl}
                    alt={testimonial.name}
                    sx={{ width: 50, height: 50, mr: 2 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      {testimonial.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 14, color: '#666' }} />
                      <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>
                        {testimonial.location}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Rating */}
                <Rating
                  value={testimonial.rating}
                  readOnly
                  size="small"
                  sx={{
                    mb: 1.5,
                    '& .MuiRating-iconFilled': {
                      color: '#facc15',
                    },
                  }}
                />

                {/* Comment */}
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#333',
                    lineHeight: 1.6,
                    mb: 2,
                    flex: 1,
                  }}
                >
                  "{testimonial.comment}"
                </Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>
      )}

      {/* Personalized Recommendations — hidden when there is nothing to recommend */}
      {productSections.some((section) => section.products?.length > 0) && (
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2, md: 3, lg: 4 }, py: { xs: 3, sm: 4, md: 6 }, ...sectionSx('recommendations') }}>
        <SmartRecommendations
          products={productSections.flatMap(section => section.products || [])}
          title="Recommended for You"
          showFilters={true}
        />
      </Container>
      )}

      {/* Wishlist Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: 'var(--primary)',
            fontSize: '0.9rem',
            fontWeight: 500,
            borderRadius: '8px',
            boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 30%, transparent)',
          }
        }}
      />
    </Box>
  );
};

export default Home;
